import PropTypes from "prop-types";
import React, { useRef, useState, useMemo } from "react";

import { ERROR_MESSAGE, SHOW_COUNTER_THRESHOLD } from "../constants";

import "./styles.scss";

const ExpandableMessageInput = (props) => {
  const { maxLength } = props;
  const [currentMessageText, setCurrentMessageText] = useState("");
  const inputElement = useRef(null);

  const handleInput = () => {
    props.channel.startTyping();
    const text = inputElement.current.innerText;

    setCurrentMessageText(text === "\n" ? "" : text);
    props.channel.endTyping();
  };

  const resetMessageText = () => {
    setCurrentMessageText("");
    inputElement.current.textContent = "";
  };

  const handleTyping = (event) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      }
      if (maxLength && currentMessageText.length > maxLength) {
        event.preventDefault();

        return;
      }
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (maxLength && currentMessageText.length > maxLength) {
      return;
    }
    // Prevent submitting empty or whitespace-only messages
    // Note: handleInput normalizes "\n" to "", so we check trim() instead
    if (currentMessageText.trim() === "") {
      resetMessageText();

      return;
    }
    props.submitHandler(currentMessageText);
    resetMessageText();
  };

  const charCount = currentMessageText.length;
  const isOverLimit = useMemo(() => maxLength && charCount > maxLength, [maxLength, charCount]);

  const showCharCounter = useMemo(
    () => maxLength && (charCount / maxLength >= SHOW_COUNTER_THRESHOLD || isOverLimit),
    [maxLength, charCount, isOverLimit],
  );

  const inputClassName = `mb-0 h-auto form-control content-editable-input-container ${
    isOverLimit ? "input-error" : ""
  }`;

  const buttonClassName = `btn btn-outline-secondary send-button ${
    isOverLimit ? "send-button--disabled text-muted" : ""
  }`;

  return (
    <div className="w-100 tw:p-5">
      <div className="input-group">
        <p
          role="textbox"
          aria-label="Enter a message..."
          aria-invalid={isOverLimit}
          aria-describedby={
            showCharCounter
              ? isOverLimit
                ? "message-error message-counter"
                : "message-counter"
              : undefined
          }
          onInput={handleInput}
          contentEditable={true}
          onKeyDown={handleTyping}
          ref={inputElement}
          className={inputClassName}
          data-e2e="Messages__messageInput"
        />

        <div className="input-group-append">
          <span
            className={buttonClassName}
            onClick={!isOverLimit ? handleSubmit : undefined}
            data-e2e="Messages__sendMessageButton"
          >
            Send
          </span>
        </div>
      </div>
      {showCharCounter && (
        <div className="message-footer tw:text-xs">
          {isOverLimit && (
            <div id="message-error" className="tw:text-text-error-primary">
              {ERROR_MESSAGE}
            </div>
          )}
          <div
            id="message-counter"
            className={`${isOverLimit ? "tw:text-text-error-primary" : "tw:text-text-secondary"} ${!isOverLimit ? "tw:ml-auto" : ""}`}
          >
            {charCount} / {maxLength}
          </div>
        </div>
      )}
    </div>
  );
};

ExpandableMessageInput.propTypes = {
  channel: PropTypes.object.isRequired,
  submitHandler: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
};

export default ExpandableMessageInput;
